import { paths } from "@/App";
import { SignIn, SignUp, useUser } from "@clerk/react";
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { REFERRAL_CODE_KEY_URL } from "@repo/marketing-site-shared/referral-shared";
import { TP_VIDEO_INTERNAL_NAME_STORAGE_KEY } from "@/lib/session-storage";
import {
  enrichFacebookClickIds,
  loadExistingAttribution,
} from "@repo/marketing-site-shared/attribution";
import { AttributionCookieDto } from "@repo/api/attribution";

type Props = {
  type: "sign-in" | "sign-up";
};

export default function SignInPage({ type }: Props) {
  const { isSignedIn, isLoaded } = useUser();

  const toltReferralId = useToltReferralId();
  const referralCode = useReferralCode();
  const tpInternalName = useTpInternalName();
  const attribution = useAttribution();

  if (!isLoaded) {
    return null;
  }

  if (isSignedIn) {
    return <Navigate to={paths.root()} />;
  }

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center">
      <div className="absolute inset-0">
        <video
          src="/StudioBackground.mp4"
          poster="/StudioBackground.jpg"
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>

      <div className="overflow-y-auto">
        {type === "sign-in" ? (
          <SignIn />
        ) : (
          <SignUp
            unsafeMetadata={{
              attribution,
              toltReferralId,
              referralCode,
              tpInternalName,
            }}
          />
        )}
      </div>
    </div>
  );
}

// Hack to get the Tolt referral id.
// Tolt loads asynchronously, and window.tolt_referral is initially null.
// There is no event to listen to for when tolt is loaded, so we need to poll for it.
// We try to poll for the id every 200ms for 5 seconds.
function useToltReferralId(): string | null {
  const [toltReferralId, setToltReferralId] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let i = 0;

    function poll() {
      i++;

      if (!isMounted || i > 25) return;

      const toltReferralId = (window as any).tolt_referral ?? null;
      if (toltReferralId) {
        setToltReferralId(toltReferralId);
      } else {
        setTimeout(poll, 200);
      }
    }
    poll();

    return () => {
      isMounted = false;
    };
  }, []);

  return toltReferralId;
}

function useReferralCode() {
  const [searchParams] = useSearchParams();
  return searchParams.get(REFERRAL_CODE_KEY_URL);
}

function useTpInternalName() {
  const [tpInternalName] = useState(() =>
    sessionStorage.getItem(TP_VIDEO_INTERNAL_NAME_STORAGE_KEY),
  );
  return tpInternalName;
}

function useAttribution() {
  const [attribution] = useState<AttributionCookieDto | null>(() => {
    const attribution = loadExistingAttribution();

    if (attribution) {
      enrichFacebookClickIds(attribution);
    }

    return attribution;
  });

  return attribution;
}
